import { QueryClient } from '@tanstack/react-query'

/**
 * 서버 상태 기본값. 스냅샷 1회 + 구독이 원칙이라 주기 폴링을 켜지 않는다 (DP-01).
 * 갱신은 WebSocket 이벤트가 캐시를 무효화하는 방식으로 온다.
 *
 * 🔴 **이 파일이 폴링 금지 3겹의 1겹**이다. 나머지 두 겹은 ESLint 가 진다 —
 *    호출부에서 `refetchInterval` 을 쓰면 린트가 막고, 화면에서 `@tanstack/react-query` 를
 *    직접 import 하는 것도 막는다. 그래서 여기가 값을 끄는 **유일한 예외 파일**이다.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchInterval: false,
        refetchOnWindowFocus: false,
        /**
         * ⚠ 재연결 시 갱신은 **WS 재연결 → 스냅샷 재조회** 경로로 온다.
         *   여기를 켜면 같은 일을 두 경로가 해서 중복 요청이 난다.
         */
        refetchOnReconnect: false,
        staleTime: Infinity,
        retry: shouldRetry,
      },
      mutations: {
        /** 🔴 명령은 재시도하지 않는다. 멱등키가 있어도 판단은 사용자 몫이다 */
        retry: false,
      },
    },
  })
}

/**
 * 재시도 정책 — **4xx 는 재시도하지 않는다.**
 * 기본값 `retry: 1` 은 403 도 다시 쏘는데, 권한이 없는 요청을 두 번 보내는 것은 의미가 없고
 * 감사 로그만 두 줄이 된다. 401 은 재시도가 아니라 refresh 흐름이 따로 처리한다.
 *
 * ⚠ `@ctcells/core` 는 `@ctcells/api` 를 모른다(의존 방향이 반대다). 그래서 `ApiError` 를
 *   import 하지 않고 `status` 필드만 본다 — 전송 오류는 `status: 0` 이다.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: unknown }).status
  const code = typeof status === 'number' ? status : 0
  if (code >= 400 && code < 500) return false
  return failureCount < 1
}
